"use client"

import { type FC, useEffect, useState, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { Col, Row, Button } from "reactstrap"
import { useAppDispatch, useAppSelector } from "../../../reduxToolkit/hooks"
import { setCardToShow, setTotalProduct } from "../../../reduxToolkit/reducers/layoutSlice"
import type { GridViewType } from "../../../types/Product"
import FilterOffcanvas from "../FilterOffcanvas"
import FilterSidebar from "./filter"
import FilterTags from "./filter/FilterTags"
import GridLayout from "./GridLayout"
import Category from "./Category"
import TopFilter from "./filter/TopFilter"
import RightSidebar from "./rightSidebar"
import client from "../../../feathers"
import CarProductBox1Custom from "../productBox/CarProductBox1"

// Pagination Hook
const usePagination = ({
  initialPage = 1,
  initialItemsPerPage = 12,
}: {
  initialPage?: number
  initialItemsPerPage?: number
} = {}) => {
  const [currentPage, setCurrentPage] = useState(initialPage)
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage)
  const [totalItems, setTotalItems] = useState(0)

  const totalPages = Math.ceil(totalItems / itemsPerPage)

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page)
      }
    },
    [totalPages],
  )

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1)
    }
  }, [currentPage, totalPages])

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1)
    }
  }, [currentPage])

  const resetPagination = useCallback(() => {
    setCurrentPage(1)
  }, [])

  const handleItemsPerPageChange = useCallback((newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1) // Reset to first page when changing items per page
  }, [])

  return {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    setCurrentPage: goToPage,
    setItemsPerPage: handleItemsPerPageChange,
    setTotalItems,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    resetPagination,
  }
}

// Pagination Component
const Pagination: FC<{
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  onItemsPerPageChange: (itemsPerPage: number) => void
}> = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange, onItemsPerPageChange }) => {
  const getVisiblePages = () => {
    const delta = 2
    const range = []
    const rangeWithDots = []

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i)
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...")
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages)
    } else {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots
  }

  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  if (totalPages <= 1) return null

  return (
    <div
      className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-4 pt-4"
      style={{ borderTop: "1px solid #e9ecef" }}
    >
      <div className="pagination-info">
        <span className="text-muted small">
          Showing {startItem} to {endItem} of {totalItems} results
        </span>
      </div>

      <div className="d-flex align-items-center flex-wrap gap-3">
        <div className="items-per-page">
          <select
            className="form-select form-select-sm"
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            style={{ minWidth: "120px" }}
          >
            <option value={6}>6 per page</option>
            <option value={12}>12 per page</option>
            <option value={24}>24 per page</option>
            <option value={48}>48 per page</option>
          </select>
        </div>

        <nav aria-label="Page navigation">
          <ul className="pagination pagination-sm mb-0 mt-0 flex-wrap">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <Button
                className="page-link"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                size="sm"
                outline
                style={{
                  borderRadius: "0.375rem",
                  margin: "0 0.125rem",
                  fontSize: "0.875rem",
                }}
              >
                <i className="ri-arrow-left-double-fill" />
              </Button>
            </li>

            {getVisiblePages().map((page, index) => (
              <li key={index} className={`page-item ${page === currentPage ? "active" : ""}`}>
                {page === "..." ? (
                  <span
                    className="page-link"
                    style={{
                      borderRadius: "0.375rem",
                      margin: "0 0.125rem",
                      fontSize: "0.875rem",
                    }}
                  >
                    ...
                  </span>
                ) : (
                  <Button
                    className="page-link"
                    onClick={() => onPageChange(page as number)}
                    size="sm"
                    outline={page !== currentPage}
                    color={page === currentPage ? "primary" : "secondary"}
                    style={{
                      borderRadius: "0.375rem",
                      margin: "0 0.125rem",
                      fontSize: "0.875rem",
                    }}
                  >
                    {page}
                  </Button>
                )}
              </li>
            ))}

            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
              <Button
                className="page-link"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                size="sm"
                outline
                style={{
                  borderRadius: "0.375rem",
                  margin: "0 0.125rem",
                  fontSize: "0.875rem",
                }}
              >
                <i className="ri-arrow-right-double-fill" />
              </Button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  )
}

// Main GridView Component
const GridView: FC<GridViewType> = ({
  type,
  side,
  gridSize,
  sectionClass,
  gridType,
  view,
  topFilterSidebar,
  offcanvasSide,
  scrollType,
  map,
  mapSide,
  modalType,
  filterTagsClass,
  carShow,
  topCategory,
  topFilter,
  jobBoxStyle,
  fullList,
}) => {
  const [searchParams] = useSearchParams()
  const budget = searchParams.get("budget")
  const kilometers = searchParams.get("kilometers")
  const category = searchParams.get("category")
  const dispatch = useAppDispatch()
  const [data, setData] = useState<any[]>([])
  const [searchName, setSearchname] = useState("")
  const [loading, setLoading] = useState(false)

  const { sortValue } = useAppSelector((state) => state.filter)

  // Initialize pagination
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    setCurrentPage,
    setItemsPerPage,
    setTotalItems,
    resetPagination,
  } = usePagination({
    initialPage: 1,
    initialItemsPerPage: carShow || 12,
  })

  const fetchProductData = async (
    searchName: string,
    sortValue: any,
    category: any,
    budget: any,
    kilometers: any,
    page = 1,
    limit = 12,
  ) => {
    setLoading(true)

    const searchParams: Record<string, any> = {}
    if (category) searchParams["categoryName"] = category
    if (kilometers) searchParams["kilometers"] = kilometers
    if (budget) searchParams["budget"] = budget

    // Add pagination parameters
    const skip = (page - 1) * limit

    try {
      const badgeFilter = sortValue?.badgeFilter;
      const sortParams = badgeFilter ? {} : sortValue;

      const res = await client.service("products").find({
        query: {
          ...searchParams,
          orRegex: { name: searchName },
          status: true,
          ...(badgeFilter ? { badge: badgeFilter.toLowerCase() } : {}),
          $sort: {
            ...sortParams,
            createdAt: -1,
          },
          $limit: limit,
          $skip: skip,
        },
      })

      setData(res.data || [])
      setTotalItems(res.total || 0)
      dispatch(setTotalProduct(res?.total || 0))
    } catch (err) {
      console.error("Error fetching products:", err)
      setData([])
      setTotalItems(0)
    } finally {
      setLoading(false)
    }
  }

  // Reset pagination and re-fetch when filters change
  useEffect(() => {
    resetPagination()
    // fetch page 1 immediately when filters change, bypassing currentPage state update delay
    const handler = setTimeout(() => {
      fetchProductData(searchName, sortValue, category, budget, kilometers, 1, itemsPerPage)
    }, 500)
    return () => clearTimeout(handler)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchName, sortValue, category, budget, kilometers])

  // Fetch when page/pageSize changes (not filter changes — those are handled above)
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProductData(searchName, sortValue, category, budget, kilometers, currentPage, itemsPerPage)
    }, 500)
    return () => clearTimeout(handler)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage])

  useEffect(() => {
    dispatch(setCardToShow(carShow || 6))
  }, [carShow, dispatch])

  const { productItem } = useAppSelector((state) => state.product)

  const mergeData = data

  const showProduct = productItem.filter((item) => item.type === type)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
  }

  return (
    <>
      <section
        className={`${type !== "job" ? "section-t-md-space section-b-md-space" : ""} ${sectionClass ? sectionClass : ""}`}
      >
        {topCategory && <Category />}
        <div className={fullList ? "container-fluid" : gridSize === 4 ? "custom-container" : "container"}>
          <Row>
            {side !== "no" && (
              <Col xl="3" className={`filter-sidebar ${side === "right" ? " order-1" : ""}`}>
                <FilterSidebar value={showProduct} modalType={modalType} type={type} />
              </Col>
            )}
            <div className={side === "no" ? "col-lg-12" : side === "both" ? "col-xl-6 col-lg-8" : "col-xl-9"}>
              {topFilter && type === "car" && <TopFilter />}
              <FilterTags
                side={side}
                topFilterSidebar={topFilterSidebar}
                mainClass={filterTagsClass}
                type={type}
                jobBoxStyle={jobBoxStyle}
                searchName={searchName}
                setSearchname={setSearchname}
              />

              {loading && (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {map ? (
                <Row className="gy-4">
                  <Col xl="6" className={`map-section ${mapSide === "right" ? "order-1" : ""}`}>
                    <div id="map">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d20281.52037088709!2d56.051818439171534!3d25.89548611084295!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef67bc7d52f2459%3A0xa922da11fbe1044d!2zQWxqb29kIFJlc29ydCDYpdiz2KrYsdin2K3YqSDYp9mE2KzZiNiv!5e0!3m2!1sen!2sin!4v1731671548714!5m2!1sen!2sin"
                        width={650}
                        height={800}
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Google Maps Embed of Toronto"
                      />
                    </div>
                  </Col>
                  <GridLayout
                    value={mergeData || []}
                    type={type}
                    gridSize={gridSize}
                    gridType={gridType}
                    view={view}
                    scrollType={scrollType}
                    map={map}
                    jobBoxStyle={jobBoxStyle}
                    side={side}
                  />
                </Row>
              ) : (
                <>
                  <Row className="gy-4 ratio_65">
                    {!loading && mergeData.length === 0 && (
                      <Col xs="12">
                        <div className="text-center py-5">
                          <h4>No products found</h4>
                          <p className="text-muted">Try adjusting your search criteria</p>
                        </div>
                      </Col>
                    )}
                    {mergeData.map((item, index) => (
                      <Col
                        xxl="3"
                        lg="4"
                        sm="6"
                        data-aos="fade-up"
                        data-aos-duration={200 * (index + 1)}
                        key={`${item.id || index}-${currentPage}`}
                      >
                        <CarProductBox1Custom data={item} />
                      </Col>
                    ))}
                  </Row>

                  {/* Pagination Component */}
                  {totalItems > 0 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      itemsPerPage={itemsPerPage}
                      onPageChange={handlePageChange}
                      onItemsPerPageChange={handleItemsPerPageChange}
                    />
                  )}
                </>
              )}
            </div>
            {side === "both" && <RightSidebar value={showProduct} />}
          </Row>
        </div>
      </section>
      <FilterOffcanvas modalType={modalType} type={type} offcanvasSide={offcanvasSide} />
    </>
  )
}

export default GridView
